// Copyright (c) 2026 Or2 LLC. All rights reserved.
// Insight Platform - proprietary software by Nnaico (https://nnaico.com).
// See the LICENSE file in this repository. All rights reserved.

import { useState } from 'react';
import { Box, Typography, Chip, Paper, Checkbox, alpha } from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import FolderIcon from '@mui/icons-material/Folder';
import { resolveWriteDecision, getWriteOutcome } from '../../lib/localFiles';

/**
 * Write confirmation card (local_write_file tool).
 *
 * Every write to the user's local folder is approved here before it lands on
 * disk — unless the folder is TRUSTED (shield on the 📁 chip), in which case
 * the card only records the auto-approved write for audit.
 *
 * The decision lives in a module-level map (getWriteOutcome) so the card
 * keeps its final state across message re-renders/remounts.
 *
 * Props (match the `write_confirm` part pushed by ChatInterfaceFull):
 *   callId     : string   — ties the card to the pending tool call
 *   path       : string   — path relative to the connected folder
 *   isNew      : boolean  — file does not exist yet
 *   trusted    : boolean  — folder is trusted → auto-approved (no buttons)
 *   oldIsBinary: boolean  — existing file is binary (no line diff possible)
 *   oldSize    : number   — bytes of the existing file (0 if new)
 *   newSize    : number   — bytes of the new content
 *   diff       : { lines: [{t:'ctx'|'add'|'del', s}], truncated } | null
 *   preview    : string|null — first lines of new content when no diff
 */
export default function WriteConfirmCard({
  callId,
  path,
  isNew = false,
  trusted = false,
  oldIsBinary = false,
  oldSize = 0,
  newSize = 0,
  diff = null,
  preview = null,
}) {
  // Restore the outcome if this card was already decided (remount-safe)
  const [decision, setDecision] = useState(() => getWriteOutcome(callId) ?? (trusted ? 'auto' : null));
  const [remember, setRemember] = useState(false);

  const decide = (approved) => {
    if (decision) return;
    console.debug('[local-write] card decide', callId, approved, 'remember:', remember);
    setDecision(approved ? 'approved' : 'rejected');
    resolveWriteDecision(callId, approved, approved && remember);
  };

  const fmt = (b) => {
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  };

  const lineStyle = {
    fontFamily: 'monospace',
    fontSize: '0.75rem',
    lineHeight: 1.6,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
    px: 1.5,
  };

  return (
    <Paper
      elevation={0}
      sx={{
        my: 1.5,
        p: 2,
        border: (t) => `1px solid ${t.palette.divider}`,
        borderRadius: 3,
        background: (t) =>
          t.palette.mode === 'dark'
            ? `linear-gradient(180deg, ${alpha(t.palette.background.paper, 0.6)} 0%, ${alpha(t.palette.background.paper, 0.2)} 100%)`
            : `linear-gradient(180deg, ${alpha(t.palette.warning.main, 0.03)} 0%, ${alpha(t.palette.background.paper, 0.9)} 100%)`,
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <FolderIcon sx={{ fontSize: 18, color: 'primary.main' }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>
          {path}
        </Typography>
        <Chip
          size="small"
          label={isNew ? 'New file' : 'Overwrite'}
          color={isNew ? 'success' : 'warning'}
          variant="outlined"
          sx={{ borderRadius: 1.5, fontWeight: 600 }}
        />
        <Typography variant="caption" color="text.secondary">
          {isNew ? `${fmt(newSize)} to write` : `${fmt(oldSize)} → ${fmt(newSize)}`}
        </Typography>
      </Box>

      {/* Diff / preview */}
      {diff ? (
        <Box
          sx={{
            border: (t) => `1px solid ${t.palette.divider}`,
            borderRadius: 2,
            overflow: 'hidden',
            mb: 1.5,
            maxHeight: 320,
            overflowY: 'auto',
            bgcolor: (t) => alpha(t.palette.background.default, 0.5),
          }}
        >
          {diff.lines.map((ln, i) => (
            <Box
              key={i}
              sx={{
                ...lineStyle,
                bgcolor:
                  ln.t === 'add'
                    ? (t) => alpha(t.palette.success.main, t.palette.mode === 'dark' ? 0.16 : 0.1)
                    : ln.t === 'del'
                      ? (t) => alpha(t.palette.error.main, t.palette.mode === 'dark' ? 0.16 : 0.1)
                      : 'transparent',
                color: ln.t === 'ctx' ? 'text.secondary' : 'text.primary',
                borderLeft: (t) =>
                  `3px solid ${ln.t === 'add' ? t.palette.success.main : ln.t === 'del' ? t.palette.error.main : 'transparent'}`,
              }}
            >
              {ln.t === 'add' ? '+ ' : ln.t === 'del' ? '- ' : '  '}
              {ln.s === '' ? '\u00A0' : ln.s}
            </Box>
          ))}
          {diff.truncated && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', p: 1 }}>
              [... diff truncated for display]
            </Typography>
          )}
        </Box>
      ) : oldIsBinary ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontStyle: 'italic' }}>
          Existing file is binary — it will be replaced entirely ({fmt(oldSize)} → {fmt(newSize)}).
        </Typography>
      ) : preview ? (
        <Box
          sx={{
            border: (t) => `1px solid ${t.palette.divider}`,
            borderRadius: 2,
            overflow: 'hidden',
            mb: 1.5,
            maxHeight: 240,
            overflowY: 'auto',
            bgcolor: (t) => alpha(t.palette.background.default, 0.5),
          }}
        >
          <Box sx={lineStyle}>{preview || '\u00A0'}</Box>
        </Box>
      ) : null}

      {/* Actions / outcome */}
      {decision === null ? (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 'auto' }}>
            <Checkbox
              size="small"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              sx={{ p: 0.5 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => setRemember(!remember)}>
              Always approve writes to this folder
            </Typography>
          </Box>
          <Box
            component="button"
            type="button"
            onClick={() => decide(false)}
            sx={{
              px: 2, py: 0.85, border: 'none', borderRadius: 2, cursor: 'pointer',
              fontSize: '0.85rem', fontWeight: 600, fontFamily: 'inherit',
              color: (t) => t.palette.error.contrastText,
              background: (t) => alpha(t.palette.error.main, 0.85),
              transition: 'all 0.18s ease',
              '&:hover': { background: (t) => t.palette.error.main },
            }}
          >
            Reject
          </Box>
          <Box
            component="button"
            type="button"
            onClick={() => decide(true)}
            sx={{
              px: 2, py: 0.85, border: 'none', borderRadius: 2, cursor: 'pointer',
              fontSize: '0.85rem', fontWeight: 600, fontFamily: 'inherit',
              color: (t) => t.palette.success.contrastText,
              background: (t) => t.palette.success.main,
              transition: 'all 0.18s ease',
              '&:hover': { background: (t) => t.palette.success.dark },
            }}
          >
            Approve write
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
          <Chip
            size="small"
            icon={decision === 'rejected' ? <CancelRoundedIcon /> : <CheckCircleRoundedIcon />}
            label={
              decision === 'approved'
                ? 'Approved ✓'
                : decision === 'auto'
                  ? 'Auto-approved — trusted folder'
                  : decision === 'rejected'
                    ? 'Rejected'
                    : 'Expired'
            }
            color={decision === 'rejected' ? 'error' : decision === 'approved' || decision === 'auto' ? 'success' : 'default'}
            variant="outlined"
            sx={{ fontWeight: 600, borderRadius: 1.5 }}
          />
        </Box>
      )}
    </Paper>
  );
}
